// ─────────────────────────────────────────────────────────────────────────────
// Platform Summary — Excel export
//
// Same feature as the standalone repo's PlatformSummaryPanel.tsx export:
// metadata header rows, styled column header, data rows with per-metric
// number formatting (percent / hours / count), a bold grand-total row,
// column widths, and the same Excel-formula-injection guard (a value
// starting with =, +, -, or @ gets a leading apostrophe so Excel treats it
// as text, not a formula).
//
// ─────────────────────────────────────────────────────────────────────────────

import { createExcelWorkbook, downloadExcelWorkbook, setExcelColumnWidths } from './excelExport';
import { PlatformSummaryMetrics } from '../services/automationInsightsService';

const EXCEL_INJECTION_PREFIX_PATTERN = /^\s*[=+\-@]/;

// Display-only label for ticket type — the underlying value stays "RITM"
// everywhere (API contract, SNOW enum, query params). Only what's written
// into the exported cells reads "SR", matching the on-screen table.
const TICKET_TYPE_DISPLAY_LABEL: Record<'ALL' | 'INC' | 'RITM', string> = {
  ALL: 'ALL',
  INC: 'INC',
  RITM: 'SR',
};

const HEADER_FILL = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFF2F4F5' } };
const THIN_BORDER_COLOR = 'D0D7DE';
const DATA_BORDER_COLOR = 'E1E5E9';
const TOTAL_TOP_BORDER_COLOR = 'B8C0C7';

function thinBorder(color: string) {
  return {
    top: { style: 'thin' as const, color: { argb: `FF${color}` } },
    left: { style: 'thin' as const, color: { argb: `FF${color}` } },
    bottom: { style: 'thin' as const, color: { argb: `FF${color}` } },
    right: { style: 'thin' as const, color: { argb: `FF${color}` } },
  };
}

function sanitizeExcelText(value: string): string {
  if (!value) return value;
  return EXCEL_INJECTION_PREFIX_PATTERN.test(value) ? `'${value}` : value;
}

function formatExportTimestamp(date: Date): string {
  const pad = (v: number) => String(v).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}_${pad(date.getHours())}-${pad(date.getMinutes())}`;
}

function formatHumanTimestamp(date: Date): string {
  const pad = (v: number) => String(v).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export interface MetricPresentation {
  text: string;
  excelValue: number | string | null;
  excelNumFmt?: string;
}

export interface ExportColumn {
  id: string;
  label: string;
}

export interface PlatformSummaryExportSnapshot {
  generatedAt: Date;
  selectedTicketType: 'ALL' | 'INC' | 'RITM';
  selectedSourceFilter: string;
  /** 'created' | 'resolved' — which basis this export was generated under.
   * Optional so callers that don't have the basis toggle (none currently)
   * still compile. Added 2026-10. */
  selectedBasis?: string;
  showTicketTypeColumn: boolean;
  visibleMetricColumns: ExportColumn[];
  rows: Array<{ platform_key: string; platform_name: string; ticket_type: string } & PlatformSummaryMetrics>;
  grandTotal?: PlatformSummaryMetrics;
  dateFrom: string;
  dateTo: string;
  filterSummaryLabel: string;
  platformLabelByKey: Record<string, string>;
  getMetricPresentation: (columnId: string, value: number | undefined, row: PlatformSummaryMetrics) => MetricPresentation;
}

export async function exportPlatformSummaryToExcel(snapshot: PlatformSummaryExportSnapshot): Promise<{ fileName: string; rowCount: number }> {
  const wb = await createExcelWorkbook();
  const aoa: Array<Array<string | number | null>> = [];

  aoa.push(['Report', 'Platform Summary']);
  aoa.push(['Export Generated', formatHumanTimestamp(snapshot.generatedAt)]);
  aoa.push(['Current Ticket Type', TICKET_TYPE_DISPLAY_LABEL[snapshot.selectedTicketType] ?? snapshot.selectedTicketType]);
  if (snapshot.selectedBasis) {
    aoa.push(['Current Basis', snapshot.selectedBasis === 'resolved' ? 'Resolved' : 'Created']);
  }
  aoa.push(['Current Dashboard Filter', `${snapshot.filterSummaryLabel} | source: ${snapshot.selectedSourceFilter}`]);
  aoa.push(['Current Date Range', `${snapshot.dateFrom} to ${snapshot.dateTo}`]);
  aoa.push([]);

  const headerRowIndex = aoa.length;
  const headers = [
    'Platform',
    ...(snapshot.showTicketTypeColumn ? ['Ticket Type'] : []),
    ...snapshot.visibleMetricColumns.map((c) => c.label),
  ];
  aoa.push(headers);

  const dataStartRowIndex = aoa.length;
  snapshot.rows.forEach((row) => {
    const values: Array<string | number | null> = [
      sanitizeExcelText(snapshot.platformLabelByKey[row.platform_key] ?? row.platform_name),
      ...(snapshot.showTicketTypeColumn
        ? [sanitizeExcelText(TICKET_TYPE_DISPLAY_LABEL[row.ticket_type as 'ALL' | 'INC' | 'RITM'] ?? row.ticket_type)]
        : []),
    ];

    snapshot.visibleMetricColumns.forEach((column) => {
      const presentation = snapshot.getMetricPresentation(column.id, (row as any)[column.id], row);
      values.push(typeof presentation.excelValue === 'string' ? sanitizeExcelText(presentation.excelValue) : presentation.excelValue);
    });

    aoa.push(values);
  });
  const dataEndRowIndex = aoa.length - 1;

  let totalRowIndex: number | null = null;
  if (snapshot.grandTotal) {
    totalRowIndex = aoa.length;
    const totalValues: Array<string | number | null> = [
      'Grand total',
      ...(snapshot.showTicketTypeColumn ? ['ALL'] : []),
    ];

    snapshot.visibleMetricColumns.forEach((column) => {
      const presentation = snapshot.getMetricPresentation(column.id, (snapshot.grandTotal as any)?.[column.id], snapshot.grandTotal as PlatformSummaryMetrics);
      totalValues.push(typeof presentation.excelValue === 'string' ? sanitizeExcelText(presentation.excelValue) : presentation.excelValue);
    });

    aoa.push(totalValues);
  }

  const ws = wb.addWorksheet('Platform Summary');
  ws.addRows(aoa);

  // Style header row
  for (let c = 0; c < headers.length; c += 1) {
    const cell = ws.getCell(headerRowIndex + 1, c + 1);
    if (cell.value === null) continue;
    cell.style = {
      font: { bold: true },
      alignment: { vertical: 'middle', horizontal: 'left' },
      fill: HEADER_FILL,
      border: thinBorder(THIN_BORDER_COLOR),
    };
  }

  const metricStartCol = snapshot.showTicketTypeColumn ? 2 : 1;

  // Style data rows + apply number formats
  for (let r = dataStartRowIndex; r <= dataEndRowIndex; r += 1) {
    const rowRecord = snapshot.rows[r - dataStartRowIndex];
    for (let c = 0; c < headers.length; c += 1) {
      const cell = ws.getCell(r + 1, c + 1);
      if (cell.value === null) continue;
      const isMetricCell = c >= metricStartCol;
      cell.style = {
        alignment: { vertical: 'middle', horizontal: isMetricCell ? 'right' : 'left' },
        border: thinBorder(DATA_BORDER_COLOR),
      };

      if (isMetricCell) {
        const column = snapshot.visibleMetricColumns[c - metricStartCol];
        const presentation = snapshot.getMetricPresentation(column.id, (rowRecord as any)[column.id], rowRecord);
        if (typeof presentation.excelValue === 'number' && presentation.excelNumFmt) {
          cell.numFmt = presentation.excelNumFmt;
        }
      }
    }
  }

  // Style grand total row
  if (totalRowIndex !== null) {
    for (let c = 0; c < headers.length; c += 1) {
      const cell = ws.getCell(totalRowIndex + 1, c + 1);
      if (cell.value === null) continue;
      const isMetricCell = c >= metricStartCol;
      cell.style = {
        font: { bold: true },
        alignment: { vertical: 'middle', horizontal: isMetricCell ? 'right' : 'left' },
        border: {
          top: { style: 'medium', color: { argb: `FF${TOTAL_TOP_BORDER_COLOR}` } },
          left: { style: 'thin', color: { argb: `FF${DATA_BORDER_COLOR}` } },
          bottom: { style: 'thin', color: { argb: `FF${DATA_BORDER_COLOR}` } },
          right: { style: 'thin', color: { argb: `FF${DATA_BORDER_COLOR}` } },
        },
      };

      if (isMetricCell && snapshot.grandTotal) {
        const column = snapshot.visibleMetricColumns[c - metricStartCol];
        const presentation = snapshot.getMetricPresentation(column.id, (snapshot.grandTotal as any)[column.id], snapshot.grandTotal);
        if (typeof presentation.excelValue === 'number' && presentation.excelNumFmt) {
          cell.numFmt = presentation.excelNumFmt;
        }
      }
    }
  }

  // Column widths — simple sensible sizing (feature-equivalent, not the old
  // exact auto-fit algorithm): platform column wider, metric columns sized
  // to their header label.
  setExcelColumnWidths(ws, headers.map((label, index) => index === 0 ? 24 : Math.max(12, label.length + 4)));

  const fileName = `PlatformSummary_${snapshot.selectedTicketType}_${formatExportTimestamp(snapshot.generatedAt)}.xlsx`;
  await downloadExcelWorkbook(wb, fileName);

  return { fileName, rowCount: snapshot.rows.length };
}
